<template>
  <div>
    <TopMenu :title="this.$route.params.id ? $t('Edit Playlist') : $t('Add Playlist')">
      <template slot="right">
        <a href="#" v-on:click="playlistSave($event)">
          <span :class="canSave ? 'text-success' : 'text-muted'">
            <FontAwesomeIcon icon="check" />
          </span>
        </a>
      </template>
    </TopMenu>
    <div class="view" v-on:scroll="scrollHandler($event)">
      <DesktopBackLink :to="desktopBackRoute" :label="desktopBackLabel" />
      <transition name="fade" appear>
        <div>
          <Loader :display="loadingEditRoute" />
          <div :style="{ display: !loadingEditRoute ? 'block' : 'none' }">
            <div class="p-4 d-flex align-items-center">
              <SongCover
                v-if="$route.params.id"
                :coverUrl="playlistCoverUrl"
                :alt="playlistName + ' cover'"
                :size="60"
                class="mr-3"
              />
              <div class="flex-grow-1">
                <div class="input-group rounded">
                  <div class="input-group-prepend">
                    <span class="input-group-text rounded-left border-right-0 border-primary" id="addon"><FontAwesomeIcon icon="music" /></span>
                  </div>
                  <input type="text" v-model="playlistName" :placeholder="$t('Playlist name')" class="form-control rounded-right border-left-0 border-primary">
                </div>
              </div>
            </div>
            <div class="p-4">
              <div style="display: flex; align-items: center;">
                <span style="flex: 1"><FontAwesomeIcon icon="random" class="ml-1 text-primary" /> {{$t('Shuffle')}}</span>
                <Toggle :click="() => { this.shuffle = !this.shuffle }" :status="shuffle" />
              </div>
            </div>
            <div class="p-4">
              {{$t('Song List')}}
            </div>
            <draggable v-model="displaySongList" :options="{handle:'.drag-target'}">
              <div v-for="(song, index) in displaySongList" :key="index" class="px-1 mb-1">
                <div class="bg-dark px-4 py-1 d-block d-flex justify-content-start align-items-center rounded clickable">
                  <span :style="{ display: shuffle ? 'none' : 'block' }" class="py-2 pr-2 mr-2 text-muted drag-target"><FontAwesomeIcon icon="bars" /></span>
                  <span
                    class="py-2 pr-2 mr-2 text-muted"
                    :style="{ minWidth: '30px' }"
                  >
                    <span class="font-button" v-if="song.preview_url != null && song.preview_url !== ''" @click="previewClick(song)">
                      <span :style="{ display: !isPlaying && previewSongId === null ? 'block' : 'none' }">
                        <FontAwesomeIcon icon="play" class="text-primary"/>
                      </span>
                      <span :style="{ display: !isPlaying && previewSongId !== null && song.id === previewSongId ? 'block' : 'none' }">
                        <FontAwesomeIcon icon="pause" class="text-primary"/>
                      </span>
                    </span>
                  </span>
                  <SongCover
                    :coverUrl="song.cover_url"
                    :alt="song.title + ' cover'"
                    :size="40"
                    class="mr-3"
                  />
                  <div>
                    <p class="text-white m-0 p-0">{{ song.title }}<br>
                      <small class="text-muted">{{ song.artist }} - {{ song.minutesAndSeconds }}</small>
                    </p>
                  </div>
                  <span @click="removeSong(index)" class="far-right-option ml-auto font-button"><FontAwesomeIcon icon="times" class="text-danger i" /></span>
                </div>
              </div>
            </draggable>
          </div>
        </div>
      </transition>
      <div class="footer clickable text-primary text-center pointer" @click="$router.push(ROUTE_ADDSONGS + '?playlist=create')">
        <small class="text-center">
          <FontAwesomeIcon icon="plus" class="mr-2 text-primary" /><span class="text-muted">{{$t('Add Songs')}}</span>
        </small>
      </div>
      <OverlayLoader :displayOverlay="savingRoute" />
    </div>
  </div>
</template>

<script>
import { ROUTE_DASHBOARD, ROUTE_ADDSONGS, ROUTE_PLAYLIST, ROUTE_PLAYLISTS } from '@/const/routeNames'
import musicboxClient from '@/utils/musicboxClient'
import scrollHandler from '@/utils/scrollHandler'
import { secondsToMinAndSec } from '@/utils/numberUtils'
import draggable from 'vuedraggable'
import SongCover from '@/components/shared/SongCover.vue'
import DesktopBackLink from '@/components/shared/DesktopBackLink.vue'

export default {

  name: 'PlaylistAddEdit',

  components: { draggable, SongCover, DesktopBackLink },

  created () {
    if (this.$store.state.playlistEdit.editedPlaylist == null) {
      this.$store.commit('initEditedPlaylist')
      if (this.$route.params.id) {
        this.loadingEditRoute = true
        musicboxClient
          .getPlaylist(parseInt(this.$route.params.id))
          .then(res => {
            this.fullPlaylistUpdate(res.data)
          })
          .catch(e => {
            this.$notify.danger(this.$t('Failed to get playlist data'))
          })
          .then(() => { this.loadingEditRoute = false })
      }
    }
  },

  data () {
    return {
      ROUTE_DASHBOARD,
      ROUTE_ADDSONGS,
      loadingEditRoute: false,
      savingRoute: false,
      displayLimit: 30,
      playlistCoverUrl: null,
      preview: null,
      previewSongId: null
    }
  },

  beforeDestroy () {
    if (this.preview != null) {
      this.preview.pause()
      this.preview = null
    }
  },

  computed: {
    playlistName: {
      get () { return this.$store.state.playlistEdit.editedPlaylist.name },
      set (value) { this.$store.commit('updateEditedPlaylist', { key: 'name', value }) }
    },
    shuffle: {
      get () { return this.$store.state.playlistEdit.editedPlaylist.shuffle },
      set (value) { this.$store.commit('updateEditedPlaylist', { key: 'shuffle', value }) }
    },
    songList () {
      return this.$store.state.playlistEdit.editedPlaylist.songList
    },
    displaySongList: {
      get () {
        return this.$store.state.playlistEdit.editedPlaylist.songList.slice(0, this.displayLimit)
      },
      set (value) {
        this.$store.commit('updateEditedPlaylist', {
          key: 'songListReorder',
          value: value.concat(this.$store.state.playlistEdit.editedPlaylist.songList.slice(value.length))
        })
      }
    },
    isPlaying () {
      return this.$store.state.audio.audioPlaying
    },
    desktopBackRoute () {
      if (this.$route.params.id) {
        return '/playlists/' + this.$route.params.id
      }
      return ROUTE_PLAYLISTS
    },
    desktopBackLabel () {
      if (this.$route.params.id) {
        return this.$t('Playlist Details')
      }
      return this.$t('Playlists')
    },
    canSave () {
      return this.playlistName != null &&
        this.playlistName.trim() !== '' &&
        this.songList != null &&
        this.songList.length > 0
    }
  },

  methods: {
    scrollHandler: scrollHandler('songList'),
    previewClick (song) {
      if (this.isPlaying || song.preview_url == null || song.preview_url === '') { return }
      if (this.previewSongId != null) {
        if (this.previewSongId === song.id) {
          this.preview.pause()
          this.preview = null
          this.previewSongId = null
        }
        return
      }
      this.preview = new Audio(song.preview_url)
      this.previewSongId = song.id
      this.preview.onended = () => {
        this.preview = null
        this.previewSongId = null
      }
      this.preview.play()
    },
    removeSong (index) {
      this.$store.commit('removeSongOnEditedPlaylist', index)
    },
    fullPlaylistUpdate (playlistData) {
      this.playlistName = playlistData.name
      this.shuffle = playlistData.shuffle
      this.playlistCoverUrl = playlistData.cover_url || null
      playlistData.songs.forEach(song => {
        song.minutesAndSeconds = secondsToMinAndSec(song.duration)
      })
      this.$store.commit('updateEditedPlaylist', { key: 'songList', value: playlistData.songs })
    },
    playlistSave (e) {
      e.preventDefault()
      if (!this.canSave) { return }
      const playlistPayload = {
        name: this.playlistName,
        song_ids: this.songList.map(song => song.id),
        shuffle: this.shuffle,
        available_offline: this.availableOffline
      }
      this.savingRoute = true
      if (this.$route.params.id) {
        musicboxClient
          .updatePlaylist(this.$route.params.id, playlistPayload)
          .then(res => {
            this.$store.commit('clearEditedPlaylist')
            this.$store.commit('initEditedPlaylist')
            this.fullPlaylistUpdate(res.data)
            this.$notify.success(this.$t('Playlist updated successfully'))
            this.$router.push(ROUTE_PLAYLIST.replace(':id', res.data.id))
          })
          .catch(e => {
            this.$notify.danger(this.$t('Playlist update failed'))
          })
          .then(() => { this.savingRoute = false })
      } else {
        musicboxClient
          .createPlaylist(playlistPayload)
          .then(res => {
            this.$notify.success(this.$t('Playlist created successfully'))
            this.$store.commit('clearEditedPlaylist')
            this.$router.push(ROUTE_PLAYLIST.replace(':id', res.data.id))
          })
          .catch(e => {
            if (e.response && e.response.status === 400) {
              Object.keys(e.response.data)
                .forEach(key => {
                  const ucKey = key.charAt(0).toUpperCase() + key.substr(1)
                  if (Array.isArray(e.response.data[key])) {
                    e.response.data[key].forEach(message => {
                      this.$notify.danger(this.$t(ucKey + ': ' + message))
                    })
                  } else {
                    this.$notify.danger(this.$t(ucKey + ': ' + e.response.data[key]))
                  }
                })
            } else {
              this.$notify.danger(this.$t('Playlist create failed'))
            }
          })
          .then(() => { this.savingRoute = false })
      }
    }
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/PlaylistAddEdit.vue